import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, Check, ChevronRight, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Review() {
  const { bookingId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [quote, setQuote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [subBusy, setSubBusy] = useState("");
  const [subbed, setSubbed] = useState("");

  const optIn = async (key) => {
    setSubBusy(key);
    try {
      const { data } = await api.post(`/reviews/booking/${bookingId}/recurring`, null, { params: { plan: key } });
      setSubbed(data.plan_label);
      toast.success(`You're on the ${data.plan_label} plan — we'll handle the rebooking`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not opt in");
    } finally {
      setSubBusy("");
    }
  };

  useEffect(() => {
    api.get(`/reviews/booking/${bookingId}`).then((r) => setCtx(r.data)).catch(() => setError("We couldn't find that booking."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/reviews/booking/${bookingId}`, { rating, quote });
      setDone(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save your review.");
    } finally {
      setBusy(false);
    }
  };

  const existing = done || ctx?.review;

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />
      <section className="mx-auto max-w-xl px-4 py-16" data-testid="review-page">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Your feedback</div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-100">How did we do?</h1>

        <div className="cj-card p-6 mt-8">
          {!ctx && !error && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          {ctx && (
            <>
              <div className="font-display uppercase font-extrabold text-xl text-slate-100">{ctx.service_name}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">{ctx.vehicle_label} · {ctx.scheduled_date}</div>
              <div className="my-5 h-px bg-white/5" />

              {existing ? (
                <div data-testid="review-thanks" className="text-center py-4">
                  <div className="h-12 w-12 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center"><Check className="h-6 w-6 text-emerald-300" /></div>
                  <div className="font-display uppercase font-extrabold text-xl text-slate-100 mt-3">Thanks, {ctx.customer_name.split(" ")[0]}!</div>
                  <div className="flex justify-center gap-1 mt-2">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-5 w-5 ${n <= existing.rating ? "text-[#ff6b00] fill-[#ff6b00]" : "text-slate-700"}`} />)}</div>
                  {existing.quote && <p className="text-sm text-slate-300 mt-3 italic">“{existing.quote}”</p>}
                </div>
              ) : !ctx.eligible ? (
                <div className="text-amber-300 text-sm" data-testid="review-not-eligible">Reviews open once CJ marks your detail as completed. Check back soon!</div>
              ) : (
                <>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Rating</div>
                  <div className="mt-2 flex gap-2" onMouseLeave={() => setHover(0)}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} data-testid={`review-star-${n}`} aria-label={`${n} stars`} className="transition hover:scale-110">
                        <Star className={`h-9 w-9 ${n <= (hover || rating) ? "text-[#ff6b00] fill-[#ff6b00]" : "text-slate-700"}`} />
                      </button>
                    ))}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mt-6">A line or two (optional)</div>
                  <textarea
                    value={quote}
                    onChange={(e) => setQuote(e.target.value.slice(0, 600))}
                    placeholder="What stood out? Paint, interior, punctuality…"
                    data-testid="review-quote-input"
                    className="mt-2 w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#ff6b00] min-h-[100px]"
                  />
                  <button onClick={submit} disabled={!rating || busy} data-testid="review-submit-button" className="cj-btn-primary mt-5 w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Post review <ChevronRight className="h-4 w-4" /></>}
                  </button>
                </>
              )}
            </>
          )}
          {error && <div className="mt-4 text-sm text-red-400 font-mono uppercase tracking-wider" data-testid="review-error">{error}</div>}
        </div>

        {ctx && ctx.eligible && (
          <div className="cj-card p-6 mt-6" data-testid="recurring-plans">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#ff6b00] flex items-center gap-2"><Repeat className="h-3.5 w-3.5" /> Keep it looking its best</div>
            <h2 className="font-display uppercase font-extrabold text-2xl text-slate-100 mt-2">Set & forget detailing</h2>
            {ctx.recurring_active || subbed ? (
              <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-200" data-testid="recurring-active">
                You're on the <strong>{subbed || (ctx.recurring_plans.find((p) => p.key === ctx.recurring_active)?.label) || "recurring"}</strong> plan — we'll email you to confirm and pay the deposit before each visit. Reply to any email to pause or cancel.
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-400 mt-2">Opt in to automatic rebooking on CJ's maintenance schedule. We'll email you to confirm and pay the ${ctx.deposit_amount} deposit before each visit — cancel anytime.</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {ctx.recurring_plans.map((p) => (
                    <div key={p.key} className="rounded-xl border border-white/10 bg-[#0f1522] p-4 flex flex-col" data-testid={`recurring-plan-${p.key}`}>
                      <div className="font-display uppercase font-bold text-slate-100">{p.label}</div>
                      <div className="font-mono text-[11px] text-slate-500 mt-1">{p.cadence} · {p.service_name}</div>
                      <button onClick={() => optIn(p.key)} disabled={!!subBusy} data-testid={`recurring-optin-${p.key}`} className="cj-btn-primary mt-4 px-4 py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50">
                        {subBusy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Opt in <ChevronRight className="h-4 w-4" /></>}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <Link to="/gallery" className="mt-6 inline-block text-sm text-slate-400 hover:text-slate-200">See the gallery →</Link>
      </section>
      <Footer />
    </div>
  );
}
